import { ScheduledQuery } from '@api';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import { DropdownGroup } from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { Icon } from '@design/components/icon/Icon';
import { useListScheduledQueryConversations } from '@terminal/react-query-hooks/alfred/useScheduledQueries';
import { appPaths } from '@terminal/routes/app-paths';
import { useHistorySidebarNavigation } from '@terminal/stores/use-history-sidebar-navigation/useHistorySidebarNavigation';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { DeleteScheduledTaskModal } from '../scheduled-task-detail/DeleteScheduledTaskModal';
import styles from './ScheduledTaskItem.module.scss';

interface DeletedScheduledTaskItemProps {
  task: ScheduledQuery;
}

export const DeletedScheduledTaskItem = ({
  task,
}: DeletedScheduledTaskItemProps) => {
  const { t } = useTranslation(['chat']);
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data: conversationsData } = useListScheduledQueryConversations(
    task.id,
    {},
    isDeleteModalOpen
  );
  const conversations = conversationsData?.conversations ?? [];

  const { selectedScheduledQueryId, setSelectedScheduledQueryId } =
    useHistorySidebarNavigation(state => ({
      selectedScheduledQueryId: state.selectedScheduledQueryId,
      setSelectedScheduledQueryId: state.setSelectedScheduledQueryId,
    }));

  const isActive = selectedScheduledQueryId === task.id;

  const handleClick = () => {
    setSelectedScheduledQueryId(task.id);
    navigate(appPaths.alfred);
  };

  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDropdownOpen(true);
  };

  const dropdownItems = [
    {
      label: (
        <span className={styles.criticalLabel}>
          <Icon iconName="Trash" size={16} fillColor="var(--icon-critical)" />
          {t('chat:scheduledTaskItem.delete')}
        </span>
      ),
      key: 'delete',
      onClick: () => setIsDeleteModalOpen(true),
    },
  ];

  return (
    <>
      <div
        className={clsx(styles.item, isActive && styles.isActive)}
        onClick={handleClick}
        onContextMenu={handleRightClick}
        data-testid={`deleted-task-item-${task.id}`}
      >
        <span className={styles.iconWrapper}>
          <Icon iconName="Clock" size={16} />
        </span>
        <span className={styles.title}>{task.title}</span>
        <DropdownMenu
          className={styles.dropdownContent}
          triggerNode={
            <span
              className={clsx(
                styles.dropdownTrigger,
                isDropdownOpen && styles.dropdownTriggerOpen
              )}
              onClick={e => e.stopPropagation()}
            >
              <Icon iconName="Ellipsis" size={16} />
            </span>
          }
          triggerProps={{
            onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
          }}
          contentProps={{
            align: 'end',
            side: 'bottom',
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
          }}
          open={isDropdownOpen}
          onOpenChange={setIsDropdownOpen}
        >
          <DropdownGroup items={dropdownItems} />
        </DropdownMenu>
      </div>

      {isDeleteModalOpen && (
        <DeleteScheduledTaskModal
          task={task}
          conversations={conversations}
          defaultDeleteConversations
          onClose={() => setIsDeleteModalOpen(false)}
          onDeleted={() => {
            setIsDeleteModalOpen(false);
            if (selectedScheduledQueryId === task.id) {
              setSelectedScheduledQueryId(null);
            }
          }}
        />
      )}
    </>
  );
};
